import { useParentSize } from '@visx/responsive';
import { ChartNoAxesColumn, Info } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { tableClass } from '../lib/table.ts';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { Segmented } from '../components/Segmented.tsx';
import { Skeleton } from '../components/Skeleton.tsx';

export interface LegendItem {
  key: string;
  label: string;
  colour: string;
  /** Dashed swatch: a threshold or budget line. */
  dashed?: boolean;
}

export interface ChartTable {
  columns: string[];
  rows: Array<Array<string | number>>;
}

/** What every chart in the family takes besides its data. */
export interface ChartBaseProps {
  /** States the question: "Spend per day, last 30 days". */
  title: ReactNode;
  /** One sentence the chart proves, read by screen readers: "Spend rose from $120 to $410 over 30 days". */
  summary: string;
  /** Plot height in px (default 200). Width follows the container. */
  height?: number;
  loading?: boolean;
  /** Anything thrown, or { what, why, next }. */
  error?: unknown;
  onRetry?: () => void | Promise<void>;
  /** Shown when there is nothing to draw. */
  emptyText?: { title: string; description?: string };
  /** A quiet note over partial data: "2 machines offline. Data may be stale." */
  partial?: ReactNode;
  /** Controls on the title row (a Segmented time range). */
  actions?: ReactNode;
  /** Draw without the card surface (the parent is the card). */
  frameless?: boolean;
  className?: string;
  /** Fixed width instead of measuring the container (tests, print). */
  width?: number;
  /** The title's heading level: 3 under a section heading (default), 2 when the chart comes right under the page's h1. */
  headingLevel?: 2 | 3;
}

interface ChartFrameProps extends ChartBaseProps {
  /** True when the data has nothing to draw. */
  isEmpty: boolean;
  legend?: LegendItem[];
  table: ChartTable;
  /** Draws the plot at the measured width. */
  children: (width: number) => ReactNode;
}

/**
 * The frame every chart shares (data-viz.md, Anatomy): the title, the legend in slot order,
 * the five states, a Chart or Table toggle (every chart has a table view), and an aria-label
 * summary on the plot.
 */
export function ChartFrame({
  title,
  summary,
  height = 200,
  loading,
  error,
  onRetry,
  emptyText,
  partial,
  actions,
  frameless,
  className,
  width: fixedWidth,
  headingLevel = 3,
  isEmpty,
  legend,
  table,
  children,
}: ChartFrameProps) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const { parentRef, width: measured } = useParentSize({ initialSize: { width: fixedWidth ?? 560 }, debounceTime: 50 });
  const width = fixedWidth ?? measured;
  const ready = !loading && error == null && !isEmpty;

  let body: ReactNode;
  if (loading) body = <Skeleton className="w-full rounded-card" style={{ height }} />;
  else if (error != null) body = <ErrorState error={error} onRetry={onRetry} variant="inline" />;
  else if (isEmpty)
    body = (
      <div style={{ minHeight: height }} className="flex items-center justify-center">
        <EmptyState icon={<ChartNoAxesColumn />} title={emptyText?.title ?? 'Nothing to chart yet'} description={emptyText?.description} />
      </div>
    );
  else if (view === 'table') body = <ChartDataTable table={table} label={summary} />;
  else
    body = (
      <figure role="img" aria-label={summary} className="relative m-0" style={{ height }}>
        {width > 0 && children(width)}
      </figure>
    );

  return (
    <section
      data-chart=""
      className={cn('flex min-w-0 flex-col gap-3', !frameless && 'rounded-card border border-border bg-surface p-(--space-inset-card) shadow-elevation-flat', className)}
    >
      {/* On a narrow panel the controls wrap under the title rather than squeezing it away. */}
      <header className="flex min-h-control flex-wrap items-center gap-x-3 gap-y-2">
        <Heading className="min-w-0 flex-1 basis-48 truncate text-lead text-fg">{title}</Heading>
        {(actions != null || ready) && (
          <div className="ml-auto flex shrink-0 items-center gap-3">
            {actions}
            {ready && (
              <Segmented
                label="Show as"
                size="sm"
                value={view}
                onChange={setView}
                options={[
                  { value: 'chart', label: 'Chart' },
                  { value: 'table', label: 'Table' },
                ]}
              />
            )}
          </div>
        )}
      </header>
      {ready && partial && (
        <p className="flex items-center gap-2 text-caption text-fg-muted">
          <Info className="size-3 shrink-0 text-fg-subtle" aria-hidden />
          {partial}
        </p>
      )}
      {ready && view === 'chart' && legend && legend.length > 1 && <Legend items={legend} />}
      <div ref={parentRef} className="min-w-0">
        {body}
      </div>
    </section>
  );
}

/** Swatch plus label in slot order, above the chart. The text stays in text colours; only the swatch carries the series colour. */
export function Legend({ items }: { items: LegendItem[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-fg-muted" aria-label="Legend">
      {items.map((it) => (
        <li key={it.key} className="inline-flex items-center gap-1.5">
          {it.dashed ? (
            <span aria-hidden className="inline-block h-0 w-3 border-t border-dashed border-border-strong" />
          ) : (
            <span aria-hidden className="inline-block size-2 rounded-full" style={{ background: it.colour }} />
          )}
          {it.label}
        </li>
      ))}
    </ul>
  );
}

/** The table view of a chart: the same numbers, readable without colour or a pointer. */
export function ChartDataTable({ table, label }: { table: ChartTable; label: string }) {
  return (
    <div className="max-h-80 overflow-auto rounded-control border border-border-subtle">
      <table className="w-full border-collapse text-ui" aria-label={label}>
        <thead className="sticky top-0 bg-surface">
          <tr className={tableClass.headRow}>
            {table.columns.map((c, i) => (
              <th key={c} scope="col" className={cn(tableClass.head, 'px-3', i === 0 ? 'text-left' : 'text-right')}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, ri) => (
            <tr key={ri} className={tableClass.row}>
              {r.map((v, i) => (
                <td key={i} className={cn(tableClass.cell, 'px-3', i === 0 ? cn(tableClass.name, 'text-left') : tableClass.numeric)}>
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface TooltipRow {
  key: string;
  label: string;
  colour: string;
  value: string;
}

/** The hover and focus readout: the exact x, then one row per series with its swatch and value. */
export function ChartTooltip({ left, top, heading, rows, width }: { left: number; top: number; heading: string; rows: TooltipRow[]; width: number }) {
  const flip = left > width - 200;
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none absolute z-(--z-tooltip) min-w-36 rounded-control border border-border-strong bg-raised px-2 py-1.5 text-caption text-fg shadow-elevation-1"
      style={{ left, top, transform: `translate(${flip ? 'calc(-100% - 12px)' : '12px'}, -50%)` }}
    >
      <div className="mb-1 text-fg-subtle num">{heading}</div>
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2">
          <span aria-hidden className="inline-block size-2 shrink-0 rounded-full" style={{ background: r.colour }} />
          <span className="min-w-0 flex-1 truncate text-fg-muted">{r.label}</span>
          <span className="font-medium num">{r.value}</span>
        </div>
      ))}
    </div>
  );
}
